import moment from 'moment';
import { toWei } from 'web3-utils';
import calculateAffordableGas from '../utilities/calculateAffordableGas';

import {
  PAYMENT_SEND_START,
  PAYMENT_SEND_SUBMITTED,
  PAYMENT_SEND_ABORTED,
  PAYMENT_SEND_FAILED,
  PAYMENT_SEND_CONFIRMED,
  PAYMENT_SEND_REJECTED,
  PAYMENT_ACCEPT_START,
  PAYMENT_ACCEPT_SUBMITTED,
  PAYMENT_ACCEPT_ABORTED,
  PAYMENT_ACCEPT_FAILED,
  PAYMENT_ACCEPT_CONFIRMED,
  PAYMENT_ACCEPT_REJECTED,
  PAYMENT_CANCEL_START,
  PAYMENT_CANCEL_SUBMITTED,
  PAYMENT_CANCEL_ABORTED,
  PAYMENT_CANCEL_FAILED,
  PAYMENT_CANCEL_CONFIRMED,
  PAYMENT_CANCEL_REJECTED,
  PAYMENTS_FETCHED,
  PAYMENTS_FETCH_FAILED,
  PAYMENT_DISMISSED,
  PAYMENT_PENDING,
  PAYEMNT_PENDING_FAILED,
  PAYMENT_EVENTS_FETCHED,
  PAYMENT_EVENTS_FETCH_FAILED,
  PAYMENT_LOCALLY_DISCARDED,
  PAYMENT_LOCALLY_DISCARD_FAILED,
  PAYMENTS_LOCAL_LOADED,
} from '../actionTypes';

export const paymentSendStart = payment => ({
  type: PAYMENT_SEND_START,
  payment,
});

export const paymentSendSubmitted = (transactionHash, contract, date = moment().unix()) => ({
  type: PAYMENT_SEND_SUBMITTED,
  contract,
  transactionHash,
  sentTimestamp: date,
});

export const paymentSendFailed = error => ({
  type: PAYMENT_SEND_FAILED,
  error,
});

export const paymentSendConfirmed = transactionHash => ({
  type: PAYMENT_SEND_CONFIRMED,
  transactionHash,
});

export const paymentSendRejected = transactionHash => ({
  type: PAYMENT_SEND_REJECTED,
  transactionHash,
});

export const paymentSendAborted = () => ({
  type: PAYMENT_SEND_ABORTED,
});

export const paymentSend = (
  {
    destination,
    amount,
    tokenAddress,
    gasPrice,
    gasForward,
    ethPaymentFee,
    payWithCat,
    source,
    gasLimit,
    formattedTokenAmount,
  },
  viewOptions,
) => async (dispatch, getState, { tabbyPay, errorReporter, ethGateway }) => {
  const payment = {
    destination,
    amount,
    tokenAddress,
    sender: getState().account.address,
  };
  try {
    return await ethGateway.process(getState, {
      create: () =>
        tabbyPay.sendPaymentTransaction(
          payment,
          gasForward,
          ethPaymentFee,
          payWithCat,
          source,
          gasPrice,
          gasLimit,
          formattedTokenAmount,
        ),
      onStart: () => dispatch(paymentSendStart(payment)),
      onSuccess: transactionHash =>
        dispatch(paymentSendSubmitted(transactionHash, tabbyPay.getActiveContract().id)),
      onError: error => dispatch(paymentSendFailed(error)),
      viewOptions,
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const paymentAcceptStart = (payment, address) => ({
  type: PAYMENT_ACCEPT_START,
  paymentTransactionHash: payment.transactionHash,
  address,
});

export const paymentAcceptSubmitted = (payment, transactionHash) => ({
  type: PAYMENT_ACCEPT_SUBMITTED,
  paymentTransactionHash: payment.transactionHash,
  transactionHash,
});

export const paymentAcceptFailed = (payment, error) => ({
  type: PAYMENT_ACCEPT_FAILED,
  paymentTransactionHash: payment.transactionHash,
  error,
});

export const paymentAcceptConfirmed = paymentTransactionHash => ({
  type: PAYMENT_ACCEPT_CONFIRMED,
  paymentTransactionHash,
});

export const paymentAcceptRejected = transactionHash => ({
  type: PAYMENT_ACCEPT_REJECTED,
  transactionHash,
});

export const paymentAcceptAborted = payment => ({
  type: PAYMENT_ACCEPT_ABORTED,
  paymentTransactionHash: payment.transactionHash,
});

export const paymentAccept = (payment, gasPrice, viewOptions) => async (
  dispatch,
  getState,
  {
    tabbyPay, ethGateway, errorReporter, notifications,
  },
) => {
  try {
    const {
      app: {
        recommendedGasPrices: { standard, safeMin },
      },
      account: { address, balance },
    } = getState();

    let affordableGasPrice;
    try {
      affordableGasPrice = calculateAffordableGas(
        tabbyPay.getContractForPayment(payment).config.acceptPaymentGasLimit,
        toWei(standard.toString(), 'gwei'),
        toWei(safeMin.toString(), 'gwei'),
        balance,
      );
    } catch (error) {
      notifications.error('You have insufficient ETH to perform this transaction right now. Try again later when gas prices are lower.');
      return null;
    }

    return await ethGateway.process(getState, {
      create: () => tabbyPay.acceptPaymentTransaction(payment, address, affordableGasPrice),
      onStart: () => dispatch(paymentAcceptStart(payment, address)),
      onSuccess: txHash => dispatch(paymentAcceptSubmitted(payment, txHash)),
      onError: e => dispatch(paymentAcceptFailed(payment, e)),
      onAbort: () => dispatch(paymentAcceptAborted(payment)),
      viewOptions,
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const paymentCancelStart = (payment, address) => ({
  type: PAYMENT_CANCEL_START,
  paymentTransactionHash: payment.transactionHash,
  address,
});

export const paymentCancelSubmitted = (payment, transactionHash) => ({
  type: PAYMENT_CANCEL_SUBMITTED,
  paymentTransactionHash: payment.transactionHash,
  transactionHash,
});

export const paymentCancelFailed = (payment, error) => ({
  type: PAYMENT_CANCEL_FAILED,
  paymentTransactionHash: payment.transactionHash,
  error,
});

export const paymentCancelConfirmed = paymentTransactionHash => ({
  type: PAYMENT_CANCEL_CONFIRMED,
  paymentTransactionHash,
});

export const paymentCancelRejected = transactionHash => ({
  type: PAYMENT_CANCEL_REJECTED,
  transactionHash,
});

export const paymentCancelAborted = payment => ({
  type: PAYMENT_CANCEL_ABORTED,
  paymentTransactionHash: payment.transactionHash,
});

export const paymentCancel = (payment, gasPrice, viewOptions) => async (
  dispatch,
  getState,
  {
    tabbyPay, ethGateway, errorReporter, notifications,
  },
) => {
  try {
    const {
      app: {
        recommendedGasPrices: { standard, safeMin },
      },
      account: { address, balance },
    } = getState();

    let affordableGasPrice;
    try {
      affordableGasPrice = calculateAffordableGas(
        tabbyPay.getContractForPayment(payment).config.cancelPaymentGasLimit,
        toWei(standard.toString(), 'gwei'),
        toWei(safeMin.toString(), 'gwei'),
        balance,
      );
    } catch (error) {
      notifications.error('You have insufficient ETH to perform this transaction right now. Try again later when gas prices are lower.');
      return null;
    }

    return await ethGateway.process(getState, {
      create: () => tabbyPay.cancelPaymentTransaction(payment, address, affordableGasPrice),
      onStart: () => dispatch(paymentCancelStart(payment, address)),
      onSuccess: txHash => dispatch(paymentCancelSubmitted(payment, txHash)),
      onError: e => dispatch(paymentCancelFailed(payment, e)),
      onAbort: () => dispatch(paymentCancelAborted(payment)),
      viewOptions,
    });
  } catch (error) {
    errorReporter(error);
    return false;
  }
};

export const paymentDismissed = payment => ({
  type: PAYMENT_DISMISSED,
  paymentTransactionHash: payment.transactionHash,
});

export const paymentPending = (event, sendType, sentTimestamp) => ({
  type: PAYMENT_PENDING,
  event,
  sendType,
  sentTimestamp,
});

export const paymentPendingFailed = error => ({
  type: PAYEMNT_PENDING_FAILED,
  error,
});

export const paymentsFetched = payments => ({
  type: PAYMENTS_FETCHED,
  payments,
});

export const paymentFetchFailed = error => ({
  type: PAYMENTS_FETCH_FAILED,
  error,
});

export const paymentEventsFetched = events => ({
  type: PAYMENT_EVENTS_FETCHED,
  events,
});

export const paymentEventsFetchFailed = error => ({
  type: PAYMENT_EVENTS_FETCH_FAILED,
  error,
});

export const paymentLocallyDiscarded = payment => ({
  type: PAYMENT_LOCALLY_DISCARDED,
  paymentTransactionHash: payment.transactionHash,
});

export const paymentLocallyDiscardFailed = error => ({
  type: PAYMENT_LOCALLY_DISCARD_FAILED,
  error,
});

export const paymentsLocallyLoaded = payments => ({
  type: PAYMENTS_LOCAL_LOADED,
  payments,
});



// WEBPACK FOOTER //
// ./src/actions/payments.js